import React, { useState, useEffect, useRef, useMemo } from 'react';
import { copyToClipboard, readFromClipboard } from '../utils/helpers';
import { useMentionSearch } from '../hooks/useMentionSearch';
import { MentionDropdown } from './MentionDropdown';
import { HydratedPost, Post, Conversation, UserData, Community, MediaAttachment, Destination, FollowerGroup, ShareTarget } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useScrollLock } from '../hooks/useScrollLock';
import { toPng } from 'html-to-image';
import html2canvas from 'html2canvas';
import { PostScreenshot } from './PostScreenshot';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { BumpedPostContent } from './BumpedPostContent';
import { fetchAllCommunities } from '../api';
import { getWeightedUserList } from '../utils/interactionUtils';
import { stickerTextStyle } from './StickerText';
import {
    CloseIcon,
    BumpIcon,
    LikeIcon,
    CommentIcon,
    BookmarkIcon,
    ImageIcon,
    VideoIcon,
    AudioIcon,
    MicIcon,
    PlusIcon,
    CameraIcon,
    GalleryIcon,
    CheckmarkIcon,
    UserIcon,
    UsersIcon,
    GlobeIcon,
    PrivateIcon,
    EyeIcon,
    EyeOffIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CopyIcon,
    PasteIcon,
    SearchIcon
} from './Icons';
import { ScreenRecorder } from '@capgo/capacitor-screen-recorder';
import { PostVideoSharePreview } from './PostVideoSharePreview';
import { STORE_ITEMS, FONT_OPTIONS } from '../constants';

const HorizontalFitIcon = ({ className = "w-3.5 h-3.5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M3 12h18M7 8l-4 4 4 4M17 8l4 4-4 4" />
    </svg>
);

const VerticalFitIcon = ({ className = "w-3.5 h-3.5" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M12 3v18M8 7l4-4 4 4M8 17l4 4 4-4" />
    </svg>
);



export const CustomFontSelect = ({ value, onChange, availableFonts, disabled = false }: { value: string, onChange: (id: string) => void, availableFonts?: string[], disabled?: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);
    // Support mapping 'default' to 'font_default' when searching
    const selectedFont = FONT_OPTIONS.find((o) => o.id === value || (value === 'default' && o.id === 'font_default'));

    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const handleClick = (e: MouseEvent | TouchEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('touchstart', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('touchstart', handleClick);
        };
    }, []);

    const fontsToRender = FONT_OPTIONS.filter((o) => !availableFonts || availableFonts.includes(o.id) || o.id === 'default' || o.id === 'font_default');

    return (
        <div className="relative w-full" ref={ref}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className="w-full bg-white/5 hover:bg-white/10 px-4 py-3 rounded-2xl transition-all border border-white/10 text-[10px] font-black uppercase tracking-widest text-blue-400 focus:outline-none flex justify-center items-center truncate relative"
                style={{ fontFamily: selectedFont?.family }}
            >
                <span className="truncate">{selectedFont ? selectedFont.name : 'Select Font'}</span>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-[999] max-h-64 overflow-y-auto no-scrollbar">
                    {fontsToRender.map((font) => (
                        <button
                            key={font.id}
                            type="button"
                            onClick={() => {
                                onChange(font.id);
                                setIsOpen(false);
                            }}
                            className={`w-full text-center px-4 py-3 text-white transition-colors hover:bg-white/10 text-[16px] ${(value === font.id || (value === 'default' && font.id === 'font_default')) ? 'bg-white/20 font-bold' : ''}`}
                            style={{ fontFamily: font.family }}
                        >
                            {font.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};


const usePostTheme = (post: Post | HydratedPost | null) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData[currentUser || ''];
    const postBlurOpacity = currentUserData?.postBlurOpacity ?? 50;

    const themeColor = post?.themeColor;

    const postColorRgb = useMemo(() => {
        if (!themeColor) return null;
        try {
            let hex = themeColor.replace('#', '');
            if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
            }
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            return isNaN(r) || isNaN(g) || isNaN(b) ? null : `${r}, ${g}, ${b}`;
        } catch (e) {
            return null;
        }
    }, [themeColor]);

    const modalBg = useMemo(() => {
        if (postColorRgb) {
            return `rgba(${postColorRgb}, ${postBlurOpacity / 100})`;
        }

        const userThemeColor = currentUserData?.appThemeColor || '#161b22';
        let hex = userThemeColor.replace('#', '');
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            return `rgba(${r}, ${g}, ${b}, ${postBlurOpacity / 100})`;
        }

        return 'var(--app-theme-bg)';
    }, [postColorRgb, postBlurOpacity, currentUserData?.appThemeColor]);

    return modalBg;
};

export const BumpActionModal = ({ isOpen, onClose, onConfirm, onRemove, post }: { isOpen: boolean, onClose: () => void, onConfirm: () => void, onRemove: () => void, post?: Post | HydratedPost | null }) => {
    const modalBg = usePostTheme(post || null);
    useScrollLock(isOpen);
    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] animate-fade-in"
            onClick={onClose}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
        >
            <div className="rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center backdrop-blur-md" style={{ backgroundColor: modalBg }} onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl font-bold mb-4">Bump Action</h2>
                <p className="text-gray-300 mb-6">
                    You've already bumped this. What would you like to do?
                </p>
                <div className="flex flex-col gap-4">
                    <button onClick={onConfirm} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full transition-colors w-full">
                        Bump Again
                    </button>
                    <button onClick={onRemove} className="border border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold py-3 px-6 rounded-full transition-colors w-full">
                        Remove Bump
                    </button>
                    <button onClick={onClose} className="text-gray-400 hover:text-white font-bold py-3 px-6 rounded-full transition-colors w-full">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export const BumpModal = ({ isOpen, onClose, onConfirm, onNavigateToProfile, onNavigateToHashtag, post, currentUser, usersData }: { isOpen: boolean, onClose: () => void, onConfirm: (data: Partial<Post>) => void, onNavigateToProfile: (userId: string) => void, onNavigateToHashtag: (tag: string) => void, post: HydratedPost | null, currentUser?: string | null, usersData: Record<string, UserData> }) => {
    const modalBg = usePostTheme(post);
    const currentUserData = usersData[currentUser || ''];
    useScrollLock(isOpen);
    const [caption, setCaption] = useState('');
    const { mentionQuery, updateSearch, completeMention } = useMentionSearch();
    const [postTarget, setPostTarget] = useState<'profile' | 'community'>('profile');
    const [communities, setCommunities] = useState<Community[]>([]);
    const [search, setSearch] = useState('');
    const [selectedCommunity, setSelectedCommunity] = useState<string | null>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    // Customization states
    const [selectedFontId, setSelectedFontId] = useState('font_default');
    const [selectedPostcardStyle, setSelectedPostcardStyle] = useState('classic_postcards');
    const [selectedThemeColor, setSelectedThemeColor] = useState('#000000');
    const [selectedThemeOpacity, setSelectedThemeOpacity] = useState(50);
    const [selectedTextColor, setSelectedTextColor] = useState('#ffffff');
    const [textSize, setTextSize] = useState<'xs' | 'sm' | 'med' | 'lg'>('med');

    useEffect(() => {
        if (!isOpen) {
            setCaption('');
            setPostTarget('profile');
            setSearch('');
            setSelectedCommunity(null);
        } else {
            if (currentUser) {
                fetchAllCommunities().then(data => {
                    if (Array.isArray(data)) {
                        setCommunities(data.filter(c => c.members?.includes(currentUser)));
                    }
                }).catch(console.error);
            }
            // Initialize customization from user defaults or post
            setSelectedPostcardStyle(currentUserData?.postcardDefault || 'classic_postcards');
            setSelectedFontId('font_default');
            setSelectedThemeColor('#000000');
            setSelectedThemeOpacity(50);
            setSelectedTextColor('#ffffff');
            setTextSize('med');
        }
    }, [isOpen, currentUser, currentUserData]);

    if (!isOpen || !post) return null;

    const canPost = (c: Community) => {
        if (c.whoCanPost === 'all') return true;
        if (c.creator === currentUser) return true;
        if (c.admins?.includes(currentUser || '')) return true;
        return false;
    };

    const communitiesToShow = communities.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c._id.toLowerCase().includes(search.toLowerCase()));

    const handleConfirm = () => {
        onConfirm({
            content: { ...post.content, text: caption },
            visibility: postTarget === 'profile' ? 'public' : 'private',
            communityId: postTarget === 'community' && selectedCommunity ? selectedCommunity : undefined,
            fontId: selectedFontId,
            postcardStyle: selectedPostcardStyle,
            themeColor: selectedThemeColor,
            themeOpacity: selectedThemeOpacity,
            textColor: selectedTextColor,
            textSize,
            originalPostId: post.id
        });
    };

    const availablePostcards = STORE_ITEMS.filter(item =>
        item.category === 'postcards' &&
        (currentUserData?.purchasedItems || []).includes(item.id)
    );

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-[150] p-4"
            onClick={onClose}
        >
            <div
                className="rounded-[2.5rem] shadow-2xl max-w-xl w-full mx-auto backdrop-blur-md max-h-[90vh] flex flex-col border border-white/10 overflow-hidden"
                style={{ backgroundColor: modalBg, color: selectedTextColor || '#ffffff' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-8 pb-4 flex items-center justify-between border-b border-white/5">
                    <h2 className="text-2xl font-black italic uppercase tracking-tighter">Bump Post</h2>
                    <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-xl transition-colors">
                        <CloseIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-8 allow-scroll">
                    {/* Caption / Content Section */}
                    <div className="space-y-4 relative">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 block px-1 opacity-50">Add a caption</label>
                        <textarea
                            className="w-full bg-black/20 p-4 text-lg rounded-2xl resize-none min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500/50 border border-white/5 custom-bump-textarea"
                            value={caption}
                            onChange={(e) => {
                                const val = e.target.value;
                                setCaption(val);
                                updateSearch(val, e.target.selectionStart);
                            }}
                            placeholder="Say something about this..."
                            style={{ color: selectedTextColor || '#ffffff' }}
                        />
                        {mentionQuery !== null && (
                            <MentionDropdown
                                query={mentionQuery}
                                following={currentUserData?.following || []}
                                usersData={usersData}
                                onSelect={(username) => {
                                    const el = document.querySelector(".custom-bump-textarea") as HTMLTextAreaElement;
                                    const pos = el?.selectionStart || caption.length;
                                    const { newText, newCursorPos } = completeMention(caption, username, pos);
                                    setCaption(newText);
                                    setTimeout(() => {
                                        if (el) {
                                            el.focus();
                                            el.setSelectionRange(newCursorPos, newCursorPos);
                                        }
                                    }, 10);
                                }}
                                position="below"
                            />
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {/* Target Section */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Post To</label>
                            <button
                                onClick={() => setPostTarget(prev => prev === 'profile' ? 'community' : 'profile')}
                                className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 px-4 py-3 rounded-2xl transition-all border border-white/10 justify-between"
                            >
                                <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">
                                    {postTarget === 'profile' ? 'Profile' : 'Community'}
                                </span>
                                {postTarget === 'profile' ? <UserIcon className="w-4 h-4 text-blue-400" /> : <UsersIcon className="w-4 h-4 text-blue-400" />}
                            </button>
                        </div>

                        {/* Text Size */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Text Size</label>
                            <div className="flex items-center gap-1 bg-white/5 p-1 rounded-2xl border border-white/10 w-full">
                                <button onClick={() => {
                                    const sizes: ('xs' | 'sm' | 'med' | 'lg')[] = ['xs', 'sm', 'med', 'lg'];
                                    const idx = sizes.indexOf(textSize);
                                    setTextSize(sizes[Math.max(0, idx - 1)]);
                                }} className="p-2 hover:bg-white/10 rounded-xl transition-all text-gray-400 hover:text-white flex-1 flex justify-center">
                                    <ChevronLeftIcon className="w-4 h-4" />
                                </button>
                                <span className="w-12 text-center text-[10px] font-black uppercase tracking-widest text-blue-400">{textSize}</span>
                                <button onClick={() => {
                                    const sizes: ('xs' | 'sm' | 'med' | 'lg')[] = ['xs', 'sm', 'med', 'lg'];
                                    const idx = sizes.indexOf(textSize);
                                    setTextSize(sizes[Math.min(sizes.length - 1, idx + 1)]);
                                }} className="p-2 hover:bg-white/10 rounded-xl transition-all text-gray-400 hover:text-white flex-1 flex justify-center">
                                    <ChevronRightIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Font Style */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Font</label>
                            <CustomFontSelect
                                value={selectedFontId}
                                onChange={setSelectedFontId}
                                availableFonts={['default', 'font_default', ...(currentUserData?.purchasedItems || [])]}
                            />
                        </div>

                        {/* Postcard Style */}
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Style</label>
                            <select
                                value={selectedPostcardStyle}
                                onChange={(e) => setSelectedPostcardStyle(e.target.value)}
                                className="w-full bg-white/5 hover:bg-white/10 px-4 py-3 rounded-2xl transition-all border border-white/10 text-[10px] font-black uppercase tracking-widest text-amber-400 focus:outline-none appearance-none cursor-pointer text-center"
                            >
                                {availablePostcards.map(postcard => (
                                    <option key={postcard.id} value={postcard.id} className="bg-[#1a1a1a]">{postcard.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {postTarget === 'community' && (
                        <div className="animate-in slide-in-from-top-2 duration-200 space-y-3">
                            <div className="relative">
                                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search communities..."
                                    className="w-full p-3 pl-11 rounded-2xl text-sm text-white bg-black/20 border border-white/5 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                                />
                            </div>
                            <div className="max-h-48 overflow-y-auto space-y-1 rounded-2xl bg-black/20 p-3 border border-white/5 no-scrollbar">
                                {communitiesToShow.length === 0 && <div className="text-gray-500 p-2 text-center text-xs">No communities found.</div>}
                                {communitiesToShow.map(c => {
                                    const allowed = canPost(c);
                                    const isSelected = selectedCommunity === c._id;
                                    return (
                                        <label
                                            key={c._id}
                                            className={`flex items-center gap-3 py-2 px-3 rounded-xl cursor-pointer transition-colors ${!allowed ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/5'}`}
                                        >
                                            <input
                                                type="radio"
                                                name="bump-community"
                                                checked={isSelected}
                                                onChange={() => allowed && setSelectedCommunity(c._id)}
                                                disabled={!allowed}
                                                className="sr-only"
                                            />
                                            <div className={`flex-none w-6 h-6 rounded-full border flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-900/50' : 'bg-transparent border-white/10'}`}>
                                                {isSelected && <CheckmarkIcon className="w-3 h-3 text-white" />}
                                            </div>
                                            {c.avatar ? (
                                                <img src={c.avatar} alt={c.name} className="w-6 h-6 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-[8px]">
                                                    {c.name[0]?.toUpperCase()}
                                                </div>
                                            )}
                                            <span className="font-bold text-sm truncate flex-1">{c.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Theme & Colors */}
                    <div className="space-y-6 pt-8 border-t border-white/10">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] px-1 opacity-50">Background</label>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-8 h-8 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: selectedThemeColor }}>
                                        <input type="color" value={selectedThemeColor} onChange={(e) => setSelectedThemeColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                                    </div>
                                    <input type="text" value={selectedThemeColor} onChange={(e) => setSelectedThemeColor(e.target.value)} className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-2 text-[9px] font-mono text-white focus:outline-none focus:border-blue-500/50" />
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(selectedThemeColor); }}
                                            className="p-1.5 bg-black/20 border border-white/5 rounded-lg hover:bg-black/40 transition-all active:scale-95"
                                            title="Copy Hex"
                                        >
                                            <CopyIcon className="w-3 h-3 opacity-60" />
                                        </button>
                                        <button
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                    const text = await readFromClipboard();
                                                    if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) setSelectedThemeColor(text);
                                                } catch (err) { }
                                            }}
                                            className="p-1.5 bg-black/20 border border-white/5 rounded-lg hover:bg-black/40 transition-all active:scale-95"
                                            title="Paste Hex"
                                        >
                                            <PasteIcon className="w-3 h-3 opacity-60" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] px-1 opacity-50">Text</label>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-8 h-8 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: selectedTextColor }}>
                                        <input type="color" value={selectedTextColor} onChange={(e) => setSelectedTextColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                                    </div>
                                    <input type="text" value={selectedTextColor} onChange={(e) => setSelectedTextColor(e.target.value)} className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-2 text-[9px] font-mono text-white focus:outline-none focus:border-blue-500/50" />
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(selectedTextColor); }}
                                            className="p-1.5 bg-black/20 border border-white/5 rounded-lg hover:bg-black/40 transition-all active:scale-95"
                                            title="Copy Hex"
                                        >
                                            <CopyIcon className="w-3 h-3 opacity-60" />
                                        </button>
                                        <button
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                    const text = await readFromClipboard();
                                                    if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) setSelectedTextColor(text);
                                                } catch (err) { }
                                            }}
                                            className="p-1.5 bg-black/20 border border-white/5 rounded-lg hover:bg-black/40 transition-all active:scale-95"
                                            title="Paste Hex"
                                        >
                                            <PasteIcon className="w-3 h-3 opacity-60" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center px-1">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50">Translucency</label>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={selectedThemeOpacity}
                                        onChange={(e) => {
                                            const val = Number(e.target.value);
                                            if (!isNaN(val) && val >= 0 && val <= 100) {
                                                setSelectedThemeOpacity(val);
                                            }
                                        }}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        className="w-12 bg-black/20 border border-white/5 rounded-xl text-[10px] font-mono text-center py-0.5 text-blue-400 focus:outline-none focus:border-blue-500/50"
                                    />
                                    <span className="text-[10px] font-mono text-blue-400">%</span>
                                </div>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={selectedThemeOpacity}
                                onChange={(e) => setSelectedThemeOpacity(parseInt(e.target.value))}
                                className="w-full h-1.5 bg-white/5 rounded-lg appearance-none cursor-pointer accent-blue-500"
                            />
                        </div>
                    </div>

                    {/* Preview of content being bumped */}
                    <div className="p-4 bg-black/20 rounded-2xl border border-white/5 opacity-80 scale-95 origin-top">
                        <BumpedPostContent post={post} onNavigateToProfile={onNavigateToProfile} onNavigateToHashtag={onNavigateToHashtag} />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-8 pt-4 flex justify-end gap-3 border-t border-white/5 bg-black/10">
                    <button onClick={onClose} className="px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-white transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={postTarget === 'community' && !selectedCommunity}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-800 disabled:text-gray-500 text-white font-bold py-3 px-8 rounded-2xl transition-all active:scale-95 shadow-xl shadow-blue-900/20"
                    >
                        Bump Post
                    </button>
                </div>
            </div>
        </div>
    );
};

export const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, post }: { isOpen: boolean, onClose: () => void, onConfirm: () => void, post?: Post | HydratedPost | null }) => {
    const modalBg = usePostTheme(post || null);
    useScrollLock(isOpen);
    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[70]"
            onClick={onClose}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
        >
            <div className="rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center backdrop-blur-md" style={{ backgroundColor: modalBg }} onClick={(e) => e.stopPropagation()}>
                <h2 className="text-2xl font-bold mb-4 text-red-500">Delete Post</h2>
                <p className="text-gray-300 mb-6">
                    Are you sure you want to permanently delete this post? This action cannot be undone.
                </p>
                <div className="flex justify-center gap-4">
                    <button onClick={onClose} className="text-gray-400 hover:text-white font-bold py-2 px-6 rounded-full transition-colors">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-full transition-colors">
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

export const RemoveConnectionModal = ({ isOpen, onClose, onConfirm, type, targetUsername = 'this user' }: { isOpen: boolean, onClose: () => void, onConfirm: () => void, type: 'friend' | 'request', targetUsername?: string }) => {
    const { currentUser, usersData } = useAuth();
    useScrollLock(isOpen);

    if (!isOpen) return null;

    const currentUserData = currentUser ? usersData[currentUser] : null;
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const getExactModalBg = (color: string, opacityVal: number) => {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacityVal})`;
    };
    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const titleText = type === 'friend' ? 'Remove Friend' : 'Cancel Request';
    const bodyText = type === 'friend'
        ? `Are you sure you want to remove ${targetUsername} from your friends?`
        : `Are you sure you want to cancel your friend request to ${targetUsername}?`;

    return (
        <div
            className="fixed inset-0 flex items-center justify-center z-[70] p-4 animate-fade-in"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={onClose}
        >
            <div
                className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col p-8 text-center shadow-lg backdrop-blur-md"
                style={{ backgroundColor: modalBg, color: userTextColor }}
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-2xl font-bold mb-4 text-red-500" style={{ ...stickerTextStyle(0.15) }}>
                    {titleText}
                </h2>
                <p className="mb-6 font-medium" style={stickerTextStyle(0.18)}>
                    {bodyText}
                </p>
                <div className="flex justify-center gap-4">
                    <button onClick={onClose} className="bg-black/20 hover:bg-black/30 font-bold py-2 px-6 rounded-full transition-colors border border-white/10" style={stickerTextStyle(0.18)}>
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-full transition-colors" style={stickerTextStyle(0.18)}>
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

export const PostOptionsModal = ({
    isOpen,
    onClose,
    onConfirm,
    post,
    isCommunityPost = false,
    isFootprintPost = false,
    showTextEditing = false,
    hideShapeOptions = false,
    postcardShape: incomingShape
}: {
    isOpen: boolean,
    onClose: () => void,
    onConfirm: (data: Partial<Post>) => void,
    post?: Post | null,
    isCommunityPost?: boolean,
    isFootprintPost?: boolean,
    showTextEditing?: boolean,
    hideShapeOptions?: boolean,
    postcardShape?: 'square' | 'wide' | 'tall' | 'wide_tall'
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData[currentUser || ''];
    const userThemeColor = currentUserData?.appThemeColor || '#3b82f6';
    const modalBg = usePostTheme(post || null);
    useScrollLock(isOpen);

    const [editText, setEditText] = useState("");
    const [visibility, setVisibility] = useState<'public' | 'followers' | 'groups'>('public');
    const [visibilityGroups, setVisibilityGroups] = useState<string[]>([]);
    const [showCaption, setShowCaption] = useState(true);
    const [showOnProfile, setShowOnProfile] = useState(true);
    const [selectedFontId, setSelectedFontId] = useState('font_default');
    const [selectedPostcardStyle, setSelectedPostcardStyle] = useState('classic_postcards');
    const [selectedThemeColor, setSelectedThemeColor] = useState('#000000');
    const [selectedThemeOpacity, setSelectedThemeOpacity] = useState(50);
    const [selectedTextColor, setSelectedTextColor] = useState('#ffffff');
    const [textSize, setTextSize] = useState<'xs' | 'sm' | 'med' | 'lg'>('med');
    const [postcardShape, setPostcardShape] = useState<'square' | 'wide' | 'tall' | 'wide_tall'>('wide');
    const [media, setMedia] = useState<any[]>([]);
    const [localCoverImageFit, setLocalCoverImageFit] = useState<'auto' | 'horizontal' | 'vertical'>('auto');
    const { mentionQuery, updateSearch, completeMention } = useMentionSearch();

    const sizes: ('xs' | 'sm' | 'med' | 'lg')[] = ['xs', 'sm', 'med', 'lg'];
    const cycleSize = (dir: 1 | -1) => {
        const idx = sizes.indexOf(textSize);
        const nextIdx = Math.max(0, Math.min(sizes.length - 1, idx + dir));
        setTextSize(sizes[nextIdx]);
    };

    useEffect(() => {
        if (isOpen) {
            if (post) {
                setEditText(post.content.text || "");
                const v = post.visibility as any;
                setVisibility(v === 'private' ? 'followers' : (v || 'public'));
                setVisibilityGroups(post.visibilityGroups || []);
                setShowCaption(post.showCaption ?? true);
                setShowOnProfile(post.showOnProfile ?? true);
                setSelectedFontId(post.fontId || 'font_default');
                setSelectedPostcardStyle(post.postcardStyle || 'classic_postcards');
                setSelectedThemeColor(post.themeColor || '#000000');
                setSelectedThemeOpacity(post.themeOpacity ?? 50);
                setSelectedTextColor(post.textColor || '#ffffff');
                setTextSize(post.textSize || 'med');
                // Support legacy mapping: wider->wide, taller/rect/tall->tall
                const s = post.postcardShape as any;
                const normalized = (s === 'wide' || s === 'wider') ? 'wide' :
                    (s === 'tall' || s === 'taller' || s === 'rect') ? 'tall' : 'square';
                setPostcardShape(normalized as any);
                setMedia(post.content.media || []);
                setLocalCoverImageFit(post.content.coverImageFit || 'auto');
            } else {
                // Reset to defaults for new post customization
                setEditText("");
                setVisibility('public');
                setVisibilityGroups([]);
                setShowCaption(true);
                setShowOnProfile(true);
                setSelectedFontId('font_default');
                setSelectedPostcardStyle(currentUserData?.postcardDefault || 'classic_postcards');
                setSelectedThemeColor('#000000');
                setSelectedThemeOpacity(50);
                setSelectedTextColor('#ffffff');
                setTextSize('med');
                setPostcardShape(incomingShape || 'wide');
                setMedia([]);
                setLocalCoverImageFit('auto');
            }
        }
    }, [isOpen, post?.id || post?.content?.text]); // Using id or content as a stable-ish trigger for new posts

    if (!isOpen) return null;

    const handleSave = () => {
        onConfirm({
            content: post ? { ...post.content, text: editText, media: media, coverImageFit: localCoverImageFit } : { text: editText, media: media, coverImageFit: localCoverImageFit },
            visibility: visibility === 'followers' ? 'followers' : visibility, // Map 'private' to 'followers' if needed, but we use new strings
            visibilityGroups: visibility === 'groups' ? visibilityGroups : [],
            showCaption,
            showOnProfile,
            fontId: selectedFontId,
            postcardStyle: selectedPostcardStyle,
            themeColor: selectedThemeColor,
            themeOpacity: selectedThemeOpacity,
            textColor: selectedTextColor,
            textSize,
            postcardShape
        });
        onClose();
    };

    const availablePostcards = STORE_ITEMS.filter(item =>
        item.category === 'postcards' &&
        (usersData[currentUser || '']?.purchasedItems || []).includes(item.id)
    );

    return (
        <div
            className="fixed inset-0 bg-black/80 backdrop-blur-lg flex items-center justify-center z-[100]"
            onClick={onClose}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
        >
            <div
                className="rounded-[2.5rem] shadow-2xl max-w-xl w-full mx-4 backdrop-blur-md max-h-[90vh] flex flex-col border border-white/10 overflow-hidden"
                style={{ backgroundColor: modalBg, color: selectedTextColor }}
                onClick={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-8 pb-4 flex items-center justify-between">
                    <h3 className="text-2xl font-black italic uppercase tracking-tighter">Customize Post</h3>
                    <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-xl transition-colors">
                        <CloseIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-8 allow-scroll">
                    {showTextEditing && (
                        <div className="relative">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 block px-1 opacity-50">Content</label>
                            <textarea
                                className="w-full bg-black/20 p-4 text-lg rounded-2xl resize-none min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500/50 border border-white/5 custom-options-textarea"
                                value={editText}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setEditText(val);
                                    updateSearch(val, e.target.selectionStart);
                                }}
                                style={{ color: selectedTextColor }}
                                placeholder="I won't tell if you don't..."
                            />
                            {mentionQuery !== null && (
                                <MentionDropdown
                                    query={mentionQuery}
                                    following={currentUserData?.following || []}
                                    usersData={usersData}
                                    onSelect={(username) => {
                                        const el = document.querySelector(".custom-options-textarea") as HTMLTextAreaElement;
                                        const pos = el?.selectionStart || editText.length;
                                        const { newText, newCursorPos } = completeMention(editText, username, pos);
                                        setEditText(newText);
                                        setTimeout(() => {
                                            if (el) {
                                                el.focus();
                                                el.setSelectionRange(newCursorPos, newCursorPos);
                                            }
                                        }, 10);
                                    }}
                                    position="below"
                                />
                            )}
                        </div>
                    )}

                    {media.length > 0 && (
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 block px-1 opacity-50">
                                {media.length > 1 ? "Reorder Gallery & Fit" : "Media Fit"}
                            </label>
                            <div className="flex gap-4 overflow-x-auto pt-2 pb-4 px-2 no-scrollbar">
                                {media.map((item, index) => (
                                    <div key={index} className="relative flex-shrink-0 flex flex-col items-center">
                                        <div className="relative group w-24 h-24">
                                            <div className="w-24 h-24 rounded-2xl overflow-hidden border border-white/10 bg-black/40">
                                                {item.type === 'video' ? (
                                                    <video src={item.url.startsWith('blob:') ? item.url : `${item.url}#t=0.001`} className="w-full h-full object-cover" playsInline muted preload="metadata" />
                                                ) : (
                                                    <img src={item.url} className="w-full h-full object-cover" />
                                                )}
                                            </div>
                                            {media.length > 1 && (
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                                    {index > 0 && (
                                                        <button
                                                            onClick={() => {
                                                                const newMedia = [...media];
                                                                [newMedia[index], newMedia[index - 1]] = [newMedia[index - 1], newMedia[index]];
                                                                setMedia(newMedia);
                                                            }}
                                                            className="p-1.5 bg-white/20 hover:bg-white/40 rounded-full text-white"
                                                        >
                                                            <ChevronLeftIcon className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {index < media.length - 1 && (
                                                        <button
                                                            onClick={() => {
                                                                const newMedia = [...media];
                                                                [newMedia[index], newMedia[index + 1]] = [newMedia[index + 1], newMedia[index]];
                                                                setMedia(newMedia);
                                                            }}
                                                            className="p-1.5 bg-white/20 hover:bg-white/40 rounded-full text-white"
                                                        >
                                                            <ChevronRightIcon className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {media.length > 1 && (
                                                <div className="absolute -top-2 -left-2 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-[10px] font-bold text-white border-2 border-white/10">
                                                    {index + 1}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => {
                                                const newMedia = [...media];
                                                const currentFit = newMedia[index].fit || 'auto';
                                                const nextFit = currentFit === 'auto' ? 'horizontal' : currentFit === 'horizontal' ? 'vertical' : 'auto';
                                                newMedia[index] = { ...newMedia[index], fit: nextFit };
                                                setMedia(newMedia);
                                            }}
                                            className="mt-2 flex items-center justify-center min-w-[50px] h-6 transition-all text-[10px] font-black uppercase tracking-wider bg-transparent hover:text-opacity-80 cursor-pointer"
                                            style={{ color: (item.fit && item.fit !== 'auto') ? userThemeColor : 'rgba(255,255,255,0.6)' }}
                                            title="Change media fit"
                                        >
                                            {item.fit === 'horizontal' ? (
                                                <HorizontalFitIcon className="w-3.5 h-3.5" />
                                            ) : item.fit === 'vertical' ? (
                                                <VerticalFitIcon className="w-3.5 h-3.5" />
                                            ) : (
                                                "auto"
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {((post?.type === 'audio' || post?.type === 'voice') && post.content?.coverImageUrl) && (
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] mb-3 block px-1 opacity-50">Cover Image Fit</label>
                            <div className="flex gap-4 items-center">
                                <div className="w-24 h-24 rounded-2xl overflow-hidden border border-white/10 bg-black/40 flex-shrink-0 flex items-center justify-center relative">
                                    <img src={post.content.coverImageUrl} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex flex-col gap-1 items-start">
                                    <button
                                        onClick={() => {
                                            const currentFit = localCoverImageFit || 'auto';
                                            const nextFit = currentFit === 'auto' ? 'horizontal' : currentFit === 'horizontal' ? 'vertical' : 'auto';
                                            setLocalCoverImageFit(nextFit);
                                        }}
                                        className="flex items-center justify-center min-w-[50px] h-6 transition-all text-[10px] font-black uppercase tracking-wider bg-transparent hover:text-opacity-80 cursor-pointer text-white"
                                        style={{ color: localCoverImageFit !== 'auto' ? userThemeColor : 'rgba(255,255,255,0.6)' }}
                                        title="Change cover image fit"
                                    >
                                        {localCoverImageFit === 'horizontal' ? (
                                            <HorizontalFitIcon className="w-3.5 h-3.5" />
                                        ) : localCoverImageFit === 'vertical' ? (
                                            <VerticalFitIcon className="w-3.5 h-3.5" />
                                        ) : (
                                            "auto"
                                        )}
                                    </button>
                                    <span className="text-[9px] text-white/40 uppercase font-bold tracking-wider px-1">
                                        Current: {localCoverImageFit || 'auto'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-6">
                        {!isFootprintPost && (
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Visibility</label>
                                <button
                                    onClick={() => {
                                        if (visibility === 'public') {
                                            setVisibility('followers');
                                            setVisibilityGroups([]); // Default to all followers
                                        } else {
                                            setVisibility('public');
                                        }
                                    }}
                                    className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 px-4 py-3 rounded-2xl transition-all border border-white/10 justify-between"
                                >
                                    <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">
                                        {visibility === 'public' ? 'Public' : (currentUserData?.followerNickname || 'Followers')}
                                    </span>
                                    {visibility === 'public' ? <GlobeIcon className="w-4 h-4 text-blue-400" /> : <UsersIcon className="w-4 h-4 text-blue-400" />}
                                </button>

                                {(visibility === 'groups' || visibility === 'followers') && currentUserData?.followerGroups && (
                                    <div className="mt-2 p-1 rounded-2xl bg-black/20 border border-white/5 grid grid-cols-1 divide-y divide-white/5 max-h-32 overflow-y-auto no-scrollbar animate-in slide-in-from-top-2 duration-200">
                                        <label className="flex items-center gap-3 py-2 px-3 hover:bg-white/5 cursor-pointer group/item">
                                            <input
                                                type="checkbox"
                                                checked={visibilityGroups.length === 0}
                                                onChange={() => {
                                                    setVisibilityGroups([]);
                                                    setVisibility('followers');
                                                }}
                                                className="sr-only"
                                            />
                                            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${visibilityGroups.length === 0 ? 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-900/50' : 'bg-transparent border-white/20'}`}>
                                                {visibilityGroups.length === 0 && <CheckmarkIcon className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-xs truncate">All {currentUserData?.followerNickname || 'Followers'}</p>
                                            </div>
                                        </label>
                                        {currentUserData.followerGroups.filter((g: FollowerGroup) => g.id !== 'default_all').map((group: FollowerGroup) => (
                                            <label key={group.id} className="flex items-center gap-3 py-2 px-3 hover:bg-white/5 cursor-pointer group/item">
                                                <input
                                                    type="checkbox"
                                                    checked={visibilityGroups.includes(group.id)}
                                                    onChange={() => {
                                                        const nextGroups = visibilityGroups.includes(group.id)
                                                            ? visibilityGroups.filter(id => id !== group.id)
                                                            : [...visibilityGroups, group.id];
                                                        setVisibilityGroups(nextGroups);
                                                        if (nextGroups.length > 0) {
                                                            setVisibility('groups');
                                                        } else {
                                                            setVisibility('followers');
                                                        }
                                                    }}
                                                    className="sr-only"
                                                />
                                                <div className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${visibilityGroups.includes(group.id) ? 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-900/50' : 'bg-transparent border-white/20'}`}>
                                                    {visibilityGroups.includes(group.id) && <CheckmarkIcon className="w-2.5 h-2.5 text-white" />}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-bold text-xs truncate">{group.name}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Text Size</label>
                            <div className="flex items-center gap-1 bg-white/5 p-1 rounded-2xl border border-white/10 w-full">
                                <button onClick={() => cycleSize(-1)} className="p-2 hover:bg-white/10 rounded-xl transition-all text-gray-400 hover:text-white flex-1 flex justify-center">
                                    <ChevronLeftIcon className="w-4 h-4" />
                                </button>
                                <span className="w-12 text-center text-[10px] font-black uppercase tracking-widest text-blue-400">{textSize}</span>
                                <button onClick={() => cycleSize(1)} className="p-2 hover:bg-white/10 rounded-xl transition-all text-gray-400 hover:text-white flex-1 flex justify-center">
                                    <ChevronRightIcon className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Caption</label>
                            <button
                                onClick={() => setShowCaption(!showCaption)}
                                className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 px-4 py-3 rounded-2xl transition-all border border-white/10 justify-between"
                            >
                                <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">
                                    {showCaption ? 'Shown' : 'Hidden'}
                                </span>
                                {showCaption ? <EyeIcon className="w-4 h-4 text-blue-400" /> : <EyeOffIcon className="w-4 h-4 text-blue-400" />}
                            </button>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Show on Profile</label>
                            <button
                                onClick={() => setShowOnProfile(!showOnProfile)}
                                className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/10 px-4 py-3 rounded-2xl transition-all border border-white/10 justify-between"
                            >
                                <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">
                                    {showOnProfile ? 'Visible' : 'Hidden'}
                                </span>
                                {showOnProfile ? <CheckmarkIcon className="w-4 h-4 text-blue-400" /> : <CloseIcon className="w-4 h-4 text-gray-500" />}
                            </button>
                        </div>

                        {(!hideShapeOptions && post?.type !== 'text' && (post?.content?.media?.length || 0) > 0) && (
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Shape</label>
                                <select
                                    value={postcardShape}
                                    onChange={(e) => setPostcardShape(e.target.value as any)}
                                    className="w-full bg-white/5 hover:bg-white/10 px-4 py-3 rounded-2xl transition-all border border-white/10 text-[10px] font-black uppercase tracking-widest text-blue-400 focus:outline-none appearance-none cursor-pointer text-center"
                                >
                                    <option value="wide">Wide</option>
                                    <option value="square">Square</option>
                                    <option value="tall">Tall</option>
                                </select>
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Font</label>
                            <CustomFontSelect
                                value={selectedFontId}
                                onChange={setSelectedFontId}
                            />
                        </div>

                        <div className={`space-y-2 ${(((!isFootprintPost ? 1 : 0) + 4 + ((!hideShapeOptions && post?.type !== 'text' && (post?.content?.media?.length || 0) > 0) ? 1 : 0)) % 2 === 0) ? 'col-span-2 lg:col-span-2' : ''}`}>
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 px-1">Style</label>
                            <select
                                value={selectedPostcardStyle}
                                onChange={(e) => setSelectedPostcardStyle(e.target.value)}
                                className="w-full bg-white/5 hover:bg-white/10 px-4 py-3 rounded-2xl transition-all border border-white/10 text-[10px] font-black uppercase tracking-widest text-amber-400 focus:outline-none appearance-none cursor-pointer text-center"
                            >
                                {availablePostcards.map(postcard => (
                                    <option key={postcard.id} value={postcard.id} className="bg-[#1a1a1a]">{postcard.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Theme & Colors */}
                    <div className="space-y-6 pt-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] px-1 opacity-50">Background</label>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-8 h-8 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: selectedThemeColor }}>
                                        <input type="color" value={selectedThemeColor} onChange={(e) => setSelectedThemeColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                                    </div>
                                    <input type="text" value={selectedThemeColor} onChange={(e) => setSelectedThemeColor(e.target.value)} className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-2 text-[9px] font-mono text-white focus:outline-none focus:border-blue-500/50" />
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(selectedThemeColor); }}
                                            className="p-1.5 bg-black/20 border border-white/5 rounded-lg hover:bg-black/40 transition-all active:scale-95"
                                            title="Copy Hex"
                                        >
                                            <CopyIcon className="w-3 h-3 opacity-60" />
                                        </button>
                                        <button
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                    const text = await readFromClipboard();
                                                    if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) setSelectedThemeColor(text);
                                                } catch (err) { }
                                            }}
                                            className="p-1.5 bg-black/20 border border-white/5 rounded-lg hover:bg-black/40 transition-all active:scale-95"
                                            title="Paste Hex"
                                        >
                                            <PasteIcon className="w-3 h-3 opacity-60" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] px-1 opacity-50">Text</label>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-8 h-8 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: selectedTextColor }}>
                                        <input type="color" value={selectedTextColor} onChange={(e) => setSelectedTextColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
                                    </div>
                                    <input type="text" value={selectedTextColor} onChange={(e) => setSelectedTextColor(e.target.value)} className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-2 text-[9px] font-mono text-white focus:outline-none focus:border-blue-500/50" />
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); copyToClipboard(selectedTextColor); }}
                                            className="p-1.5 bg-black/20 border border-white/5 rounded-lg hover:bg-black/40 transition-all active:scale-95"
                                            title="Copy Hex"
                                        >
                                            <CopyIcon className="w-3 h-3 opacity-60" />
                                        </button>
                                        <button
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                try {
                                                    const text = await readFromClipboard();
                                                    if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) setSelectedTextColor(text);
                                                } catch (err) { }
                                            }}
                                            className="p-1.5 bg-black/20 border border-white/5 rounded-lg hover:bg-black/40 transition-all active:scale-95"
                                            title="Paste Hex"
                                        >
                                            <PasteIcon className="w-3 h-3 opacity-60" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center px-1">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50">Translucency</label>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={selectedThemeOpacity}
                                        onChange={(e) => {
                                            const val = Number(e.target.value);
                                            if (!isNaN(val) && val >= 0 && val <= 100) {
                                                setSelectedThemeOpacity(val);
                                            }
                                        }}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        className="w-12 bg-black/20 border border-white/5 rounded-xl text-[10px] font-mono text-center py-0.5 text-blue-400 focus:outline-none focus:border-blue-500/50"
                                    />
                                    <span className="text-[10px] font-mono text-blue-400">%</span>
                                </div>
                            </div>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={selectedThemeOpacity}
                                onChange={(e) => setSelectedThemeOpacity(parseInt(e.target.value))}
                                className="w-full h-1.5 bg-white/5 rounded-lg appearance-none cursor-pointer accent-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-8 pt-4 flex justify-end gap-4">
                    <button
                        onClick={handleSave}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-10 rounded-2xl transition-all active:scale-95 shadow-xl shadow-blue-900/20"
                    >
                        Save Options
                    </button>
                </div>
            </div>
        </div>
    );
};

export const SharePostModal = ({ isOpen, onClose, post, conversations, usersData, posts, onShare, onExternalShare, onCrossPost, currentSlideIndex }: { isOpen: boolean, onClose: () => void, post: Post, conversations: Conversation[], usersData: Record<string, UserData>, posts: Post[], onShare: (targets: ShareTarget[], selectedBgUrl?: string) => void, onExternalShare?: () => void, onCrossPost: () => void, currentSlideIndex?: number }) => {
    const authorData = usersData[post?.author || ''];
    let fallbackColor = authorData?.appThemeColor;
    if (!fallbackColor && authorData?.profileBg?.style) {
        fallbackColor = authorData.profileBg.style.includes('backgroundColor')
            ? authorData.profileBg.style.replace('backgroundColor:', '').replace(';', '').trim()
            : authorData.profileBg.style;
    }
    const themeColor = post?.themeColor || fallbackColor || '#1a1a2e';
    const textColor = post?.textColor || '#ffffff';

    const getExactModalBg = (color: string) => {
        if (!color) return `rgba(26,26,46,0.5)`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `0.5)`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, 0.5)`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, 0.5)` : `rgba(26,26,46,0.5)`;
    };
    const modalBg = getExactModalBg(themeColor);
    useScrollLock(isOpen);
    const [search, setSearch] = useState('');
    const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([]);
    const [dmsExpanded, setDmsExpanded] = useState(true);
    const [yipyapsExpanded, setYipyapsExpanded] = useState(true);
    const [communitiesExpanded, setCommunitiesExpanded] = useState(true);
    const [communities, setCommunities] = useState<Community[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingProgress, setRecordingProgress] = useState(0);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const { currentUser } = useAuth();
    const [isDebugMode, setIsDebugMode] = useState(false);
    const [debugLogs, setDebugLogs] = useState<string[]>([]);
    const isAdmin = currentUser === 'goodlyfe' || usersData[currentUser || '']?.role === 'admin';

    const isProfileShare = (post.type as string) === 'profile';
    const isTextPostOnly = post.type === 'text' && (!post.content?.media || post.content.media.length === 0);
    const isImagePost = !isProfileShare && !isTextPostOnly &&
        Boolean(post.content?.media?.some((m: any) => m.type === 'image' || (!m.type && m.url && !m.url.match(/\.(mp4|webm|mov|avi)$/i))));
    const isVideoOrAudioPost = post.type === 'voice' || post.type === 'audio' ||
        (!isProfileShare && !isTextPostOnly && !isImagePost &&
            Boolean(post.content?.media?.some((m: any) => m.type === 'video' || (m.url && m.url.match(/\.(mp4|webm|mov|avi)$/i)))));

    const [recordingStatus, setRecordingStatus] = useState<'idle' | 'recording' | 'ready'>('idle');
    const [generatedBlob, setGeneratedBlob] = useState<Blob | null>(null);
    const [startTime, setStartTime] = useState(0);
    const [endTime, setEndTime] = useState(15);
    const [mediaDuration, setMediaDuration] = useState(0);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [sharePlatform, setSharePlatform] = useState<string | null>(null);
    const [selectedBgSlideIndex, setSelectedBgSlideIndex] = useState<number | null>(null);
    const [imageBgMode, setImageBgMode] = useState<'borderless' | 'border'>('borderless');

    const authorSlideshowImages = useMemo(() => {
        // For image posts: use the post's own gallery images as the background choices
        if (isImagePost && post.content?.media?.length) {
            return post.content.media
                .filter((m: any) => m.url && (m.type === 'image' || (!m.type && !m.url.match(/\.(mp4|webm|mov|avi)$/i))))
                .map((m: any, idx: number) => ({ url: m.url, id: `gallery-${idx}`, name: `Image ${idx + 1}` }));
        }
        // For text / profile posts: use the author's profile background slideshow
        const slideshow = usersData[post?.author || '']?.profileBgSlideshow;
        if ((!isTextPostOnly && !isProfileShare) || !slideshow?.attachments?.length) return [];
        return slideshow.attachments.filter((a: any) => a.url);
    }, [isImagePost, isTextPostOnly, isProfileShare, post?.author, post?.content?.media, usersData]);

    const logDebug = (msg: string) => {
        console.log(`[SHARE DEBUG] ${msg}`);
        setDebugLogs(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
    };



    useEffect(() => {
        if (isOpen && currentUser) {
            fetchAllCommunities().then(data => {
                if (Array.isArray(data)) {
                    setCommunities(data.filter(c => c.members?.includes(currentUser)));
                }
            }).catch(console.error);
            // Auto-select the current slide for image posts
            if (isImagePost && currentSlideIndex !== undefined && currentSlideIndex >= 0) {
                setSelectedBgSlideIndex(currentSlideIndex);
            } else if (authorSlideshowImages && authorSlideshowImages.length > 0) {
                setSelectedBgSlideIndex(0);
            } else {
                setSelectedBgSlideIndex(null);
            }
        } else {
            setSelectedTargetIds([]);
            setSearch('');
            setSelectedBgSlideIndex(null);
            setImageBgMode('borderless');
        }
    }, [isOpen, currentUser, authorSlideshowImages, isImagePost, currentSlideIndex]);
    const screenshotRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const recordingActiveRef = useRef(false);
    const recorderRef = useRef<MediaRecorder | null>(null);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);


    const selectedMedia = useMemo(() => {
        if (currentSlideIndex !== undefined && post.content.media?.[currentSlideIndex]) {
            return post.content.media[currentSlideIndex];
        }
        return post.content.media?.find(m => m.type === 'video' || m.url?.toLowerCase().endsWith('.gif')) ||
            post.content.media?.[0];
    }, [post, currentSlideIndex]);

    const isVideoShareMode = selectedMedia?.type === 'video' || selectedMedia?.url?.toLowerCase().endsWith('.gif') || post.type === 'voice' || post.type === 'audio';

    const handleVideoShare = (platform?: string) => {
        setSharePlatform(platform || null);
        setIsRecording(true);
        setRecordingStatus('recording');
        setRecordingProgress(0);
    };

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        if (isRecording && !recordingActiveRef.current) {
            const checkAndStart = () => {
                if (canvasRef.current) {
                    recordingActiveRef.current = true;
                    startRecording();
                } else if (!recordingActiveRef.current) {
                    timeoutId = setTimeout(checkAndStart, 100);
                }
            };

            const startRecording = async () => {
                if (isDebugMode) logDebug("Initiating video recording sequence...");
                if (isDebugMode) logDebug("Waiting 2000ms for assets/media initialization...");
                await new Promise(resolve => setTimeout(resolve, 2000));

                try {
                    const canvas = canvasRef.current;
                    if (!canvas) {
                        if (isDebugMode) logDebug("ERROR: Canvas ref is null!");
                        throw new Error("Canvas not ready");
                    }
                    if (isDebugMode) logDebug(`Canvas dimensions: ${canvas.width}x${canvas.height}`);

                    const canvasStream = canvas.captureStream(30);
                    const tracks = [...canvasStream.getVideoTracks()];
                    if (isDebugMode) logDebug(`Captured canvas stream. Video tracks: ${tracks.length}`);

                    if (audioStream) {
                        const audioTracks = audioStream.getAudioTracks();
                        if (isDebugMode) logDebug(`Audio stream detected. Audio tracks: ${audioTracks.length}`);
                        if (audioTracks.length > 0) {
                            tracks.push(...audioTracks);
                            if (isDebugMode) logDebug("Added audio stream tracks to recorder.");
                        }
                    }

                    // Fallback: try to capture from all video/audio elements in DOM if no audio tracks found
                    if (tracks.filter(t => t.kind === 'audio').length === 0) {
                        if (isDebugMode) logDebug("No audio tracks in capture stream. Running DOM audio track fallback scan...");
                        try {
                            const allAudios = document.querySelectorAll('audio, video');
                            for (const el of Array.from(allAudios)) {
                                try {
                                    const ms = (el as any).captureStream?.() || (el as any).mozCaptureStream?.();
                                    if (ms) {
                                        ms.getAudioTracks().forEach((t: MediaStreamTrack) => {
                                            tracks.push(t);
                                            if (isDebugMode) logDebug(`Fallback: Captured audio track from tag with src: ${(el as any).src}`);
                                        });
                                    }
                                } catch (err: any) {
                                    if (isDebugMode) logDebug(`Skipping cross-origin element: ${err.message || err}`);
                                }
                            }
                        } catch (e) {
                            if (isDebugMode) logDebug(`Fallback scan failed: ${e}`);
                            console.warn("Could not capture audio track fallback", e);
                        }
                    }

                    const stream = new MediaStream(tracks);
                    if (isDebugMode) logDebug(`Final combined stream tracks: ${stream.getTracks().length} (Video: ${stream.getVideoTracks().length}, Audio: ${stream.getAudioTracks().length})`);

                    // Determine supported mime type
                    const supportedTypes = [
                        'video/webm;codecs=vp9,opus',
                        'video/webm;codecs=vp8,opus',
                        'video/mp4;codecs=avc1,mp4a',
                        'video/webm',
                        'video/mp4'
                    ];
                    const mimeType = supportedTypes.find(type => MediaRecorder.isTypeSupported(type)) || 'video/webm';
                    if (isDebugMode) logDebug(`Selected recorder MimeType: ${mimeType}`);

                    const recorder = new MediaRecorder(stream, {
                        mimeType,
                        videoBitsPerSecond: 10000000
                    });
                    const chunks: Blob[] = [];

                    recorder.ondataavailable = (e) => {
                        if (isDebugMode) logDebug(`Data available: Chunk size = ${e.data.size} bytes`);
                        if (e.data.size > 0) chunks.push(e.data);
                    };

                    recorder.onstop = async () => {
                        if (isDebugMode) logDebug(`Recorder stopped. Total chunks collected: ${chunks.length}`);
                        const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
                        const blob = new Blob(chunks, { type: mimeType });
                        if (isDebugMode) logDebug(`Generated Blob size: ${blob.size} bytes`);
                        const fileName = `goodlyfe_story_${post.id}_${Date.now()}.${extension}`;
                        if (Capacitor.isNativePlatform()) {
                            if (isDebugMode) logDebug("Platform: Native. Writing file to local storage...");
                            const reader = new FileReader();
                            reader.readAsDataURL(blob);
                            reader.onloadend = async () => {
                                const base64data = reader.result as string;
                                const base64 = base64data.split(',')[1];

                                if (isDebugMode) logDebug(`Writing file: ${fileName} to Cache Directory`);
                                const savedFile = await Filesystem.writeFile({
                                    path: fileName,
                                    data: base64,
                                    directory: Directory.Cache
                                });
                                if (isDebugMode) logDebug(`File saved at: ${savedFile.uri}`);

                                if (isDebugMode) logDebug("Launching Native Share sheet...");
                                // Native Share FIRST
                                await Share.share({
                                    title: 'GoodLyfe Video',
                                    text: post.content.text || `Check out this ${post.type === 'audio' || post.type === 'voice' ? 'audio' : 'video'} on GoodLyfe!`,
                                    files: [savedFile.uri]
                                });
                                if (isDebugMode) logDebug("Native share completed.");

                                // Then show success modal
                                setGeneratedBlob(blob);
                                setRecordingStatus('idle');
                                setIsRecording(false);
                                setRecordingProgress(0);
                                recordingActiveRef.current = false;
                            };
                        } else {
                            // WEB SUCCESS FLOW
                            if (isDebugMode) logDebug("Platform: Web. Preparing download link...");
                            const postUrl = isProfileShare
                                ? `https://goodlyfe.net/profile/${encodeURIComponent(post.author)}`
                                : `https://goodlyfe.net/post/${post.id}`;
                            const shareText = isProfileShare ? `Follow me on GoodLyfe! ${postUrl}` : postUrl;

                            // Auto-copy link
                            try { await copyToClipboard(postUrl); if (isDebugMode) logDebug("Post link copied to clipboard."); } catch (e) { }

                            // Attempt Web Share FIRST
                            const isMobileWeb = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                            const file = new File([blob], fileName, { type: mimeType });

                            if (sharePlatform === 'download' || !(isMobileWeb && (navigator as any).share && (navigator as any).canShare && (navigator as any).canShare({ files: [file] }))) {
                                if (isDebugMode) logDebug("Initiating browser anchor download click...");
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `goodlyfe_post_${post.id}.${extension}`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                                if (isDebugMode) logDebug("Anchor download click executed.");
                            } else {
                                try {
                                    if (isDebugMode) logDebug("Attempting Web Navigator Share...");
                                    await (navigator as any).share({
                                        title: isProfileShare ? 'GoodLyfe Profile' : 'GoodLyfe Post',
                                        text: shareText,
                                        files: [file]
                                    });
                                    if (isDebugMode) logDebug("Web Navigator Share completed.");
                                } catch (e) {
                                    if (isDebugMode) logDebug(`Web Navigator Share failed: ${e}`);
                                }
                            }

                            // Clean up and close overlay
                            setGeneratedBlob(blob);
                            setRecordingStatus('idle');
                            setIsRecording(false);
                            setRecordingProgress(0);
                            recordingActiveRef.current = false;
                        }
                    };

                    recorderRef.current = recorder;

                    // SMART DURATION: Use selected range
                    const durationMs = (endTime - startTime) * 1000;

                    recorder.start();
                    if (isDebugMode) logDebug("MediaRecorder.start() called.");

                    const recordingStartTime = Date.now();
                    const progressInterval = setInterval(() => {
                        const elapsed = Date.now() - recordingStartTime;
                        const progress = Math.min(100, (elapsed / durationMs) * 100);
                        setRecordingProgress(progress);

                        if (elapsed >= durationMs || !recordingActiveRef.current) {
                            clearInterval(progressInterval);
                            if (isDebugMode) logDebug(`Progress timer elapsed. Stopping recorder...`);
                            if (recorder.state === 'recording') {
                                recorder.stop();
                            }
                        }
                    }, 100);

                } catch (e: any) {
                    if (isDebugMode) logDebug(`CRITICAL ERROR during video share generation: ${e.message || e}`);
                    console.error("Video share failed", e);
                    alert("Video share failed. Please try again.");
                    setIsRecording(false);
                    setRecordingProgress(0);
                    recordingActiveRef.current = false;
                }
            };

            checkAndStart();
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isRecording]);

    const handleCancelGeneration = () => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
            recorderRef.current.stop();
        }
        setIsRecording(false);
        setRecordingStatus('idle');
        setRecordingProgress(0);
        recordingActiveRef.current = false;
        recorderRef.current = null;
        setIsPreviewing(false);
    };

    // Auto-stop preview after 15s or reaching duration
    useEffect(() => {
        if (isPreviewing) {
            const previewTime = Math.min(15, mediaDuration > 0 ? (mediaDuration - startTime) : 15);
            const timer = setTimeout(() => {
                setIsPreviewing(false);
            }, previewTime * 1000);
            return () => clearTimeout(timer);
        }
    }, [isPreviewing, startTime, mediaDuration]);

    // Load media duration for scrubber
    useEffect(() => {
        if (!isOpen) {
            setMediaDuration(0);
            setStartTime(0);
            setEndTime(15);
            return;
        }

        const media = selectedMedia ||
            (post.type === 'audio' || post.type === 'voice' ? { url: post.content.media?.find(m => m.type === 'audio' || m.type === 'voice')?.url || post.content.media?.[0]?.url } : null);

        if (media?.url) {
            const tempMedia = document.createElement(post.type === 'audio' || post.type === 'voice' ? 'audio' : 'video');
            tempMedia.src = media.url;
            tempMedia.onloadedmetadata = () => {
                const dur = tempMedia.duration;
                setMediaDuration(dur);
                setEndTime(Math.min(dur, startTime + 15));
            };
        }
    }, [isOpen, post.id, post.type]);

    const matchedCommunity = post.communityId ? communities.find(c => c._id === post.communityId) : undefined;
    const isPrivateCommunityPost = !!matchedCommunity?.isPrivate;
    const isAuthor = currentUser === post.author;
    const restrictSharing = (isPrivateCommunityPost || post.visibility === 'private' || post.visibility === 'followers' || post.visibility === 'groups') && !isAuthor;

    const allUserIds = useMemo(() => {
        let base = Object.keys(usersData).filter(uid => uid !== post.author);
        if (restrictSharing) {
            if (isPrivateCommunityPost && matchedCommunity) {
                // Show ONLY members of the private community (excluding author/self as needed)
                base = base.filter(uid => matchedCommunity.members?.includes(uid));
            } else {
                // Private non-community post (followers, groups, private)
                const authorData = usersData[post.author];
                if (authorData) {
                    if (post.visibility === 'groups') {
                        const allowedGroupIds = post.visibilityGroups || [];
                        const userGroups = authorData.followerGroups || [];
                        const groupMembers = new Set<string>();
                        userGroups.forEach(g => {
                            if (allowedGroupIds.includes(g.id)) {
                                g.followers?.forEach(f => groupMembers.add(f));
                            }
                        });
                        base = base.filter(uid => groupMembers.has(uid));
                    } else {
                        // followers or private: show other followers of the author
                        base = base.filter(uid => (authorData.followers || []).includes(uid));
                    }
                }
            }
        }
        return base;
    }, [post, usersData, matchedCommunity, isPrivateCommunityPost, restrictSharing]);

    const userIdsToShow = useMemo(() => {
        if (!currentUser) return [];
        const baseList = getWeightedUserList(currentUser, allUserIds, usersData, conversations, communities, posts, 3).filter(uid => uid); // Remove null/undefined
        if (!search) return baseList;
        return baseList.filter(uid =>
            usersData[uid]?.displayName?.toLowerCase().includes(search.toLowerCase()) ||
            uid?.toLowerCase().includes(search.toLowerCase())
        );
    }, [currentUser, allUserIds, usersData, conversations, communities, posts, search]);

    const filteredShareTargets = useMemo(() => {
        const list: ShareTarget[] = [];

        // 1. Add Community Channels (only if sharing is not restricted to non-community, OR if it's a private community post from this community)
        const canShareToCommunities = !restrictSharing || (isPrivateCommunityPost && matchedCommunity);

        communities.forEach(comm => {
            if (isPrivateCommunityPost && matchedCommunity && comm._id !== matchedCommunity._id) {
                // If it's a private community post, we can ONLY share it to channels of THAT community
                return;
            }
            if (canShareToCommunities) {
                (comm.channels || []).filter(chan => chan.type === 'chat').forEach(chan => {
                    list.push({
                        type: 'channel',
                        id: `channel:${comm._id}:${chan.id}`,
                        name: `${comm.name} #${chan.name}`,
                        avatarUrl: comm.avatar || ''
                    });
                });
            }
        });

        // 2. Add YipYaps (Group chats)
        const canShareToYipYaps = !restrictSharing;
        if (canShareToYipYaps) {
            conversations.filter(c => c.isYipYap).forEach(c => {
                list.push({
                    type: 'yipyap',
                    id: `yipyap:${c.id}`,
                    name: (c as any).yipYapDetails?.name || c.yipYapName || 'Group Chat',
                    avatarUrl: (c as any).yipYapDetails?.avatar || ''
                });
            });
        }

        // 3. Add Users (DMs)
        userIdsToShow.forEach(uid => {
            const user = usersData[uid];
            list.push({
                type: 'dm',
                id: `dm:${uid}`,
                name: user?.displayName || uid,
                avatarUrl: user?.pfp || ''
            });
        });

        return list.filter(t => t.name?.toLowerCase().includes(search.toLowerCase()));
    }, [conversations, communities, usersData, currentUser, search, restrictSharing, isPrivateCommunityPost, matchedCommunity, userIdsToShow]);

    const groupedShareTargets = useMemo(() => {
        const dms = filteredShareTargets.filter(t => t.type === 'dm');
        const yipyaps = filteredShareTargets.filter(t => t.type === 'yipyap');
        const channels = filteredShareTargets.filter(t => t.type === 'channel');
        return { dms, yipyaps, channels };
    }, [filteredShareTargets]);




    const handleSocialShare = async (platform: string, type: 'post' | 'story' | 'message' | 'download' | 'copy') => {
        const isPublic = post.visibility === 'public';
        const isAuthor = currentUser === post.author;

        if (!isPublic && !isProfileShare && !isAuthor) {
            alert("Private posts cannot be shared externally.");
            return;
        }

        const postUrl = isProfileShare
            ? `https://goodlyfe.net/profile/${encodeURIComponent(post.author)}`
            : `https://goodlyfe.net/post/${post.id}`;

        let shareCaption = isProfileShare ? "Follow me on GoodLyfe!" : "Check out this post on GoodLyfe!";
        const encodedText = encodeURIComponent(`${shareCaption}\n\n${postUrl}`);
        const encodedUrl = encodeURIComponent(postUrl);

        const isVideoFormat =
            post.type === 'video' ||
            post.type === 'audio' ||
            post.type === 'voice' ||
            selectedMedia?.type === 'video' ||
            selectedMedia?.url?.toLowerCase().endsWith('.gif') ||
            selectedMedia?.url?.toLowerCase().endsWith('.webp') ||
            selectedMedia?.url?.toLowerCase().endsWith('.webm');

        // Fast path for copying URL only (skip image generation entirely)
        if (platform === 'copyurl') {
            try {
                await copyToClipboard(postUrl);
                alert("URL copied!");
            } catch (e) {
                alert("Failed to copy URL");
            }
            return;
        }

        // Fast-path for web intents (skip image generation to avoid browser popup blockers)
        if (!Capacitor.isNativePlatform() && (platform === 'twitter' || platform === 'whatsapp' || platform === 'facebook' || platform === 'threads')) {
            let shareUrl = '';
            switch (platform) {
                case 'twitter': shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}`; break;
                case 'whatsapp': shareUrl = `https://wa.me/?text=${encodedText}`; break;
                case 'facebook':
                    if (type === 'post') shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
                    else shareUrl = `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=YOUR_FB_APP_ID&redirect_uri=${encodedUrl}`;
                    break;
                case 'threads': shareUrl = `https://www.threads.net/intent/post?text=${encodedText}`; break;
            }
            if (shareUrl) window.open(shareUrl, '_blank');
            return;
        }

        // Fast-path for video/audio post formats (Skip screenshot creation entirely)
        if (isVideoFormat && (platform === 'instagram' || platform === 'tiktok' || platform === 'download' || platform === 'other')) {
            if (isDebugMode) logDebug("Bypassing screenshot capture for video post format.");
            handleVideoShare(platform);
            return;
        }

        if (isDebugMode) logDebug("Preparing screenshot capture for image/text post...");
        // Provide visual feedback immediately
        setIsGenerating(true);
        setRecordingStatus('recording');
        setRecordingProgress(10); // Start progress

        // Give React a moment to render the loading overlay
        await new Promise(resolve => setTimeout(resolve, 100));

        let dataUrl: string | null = null;
        try {
            if (screenshotRef.current) {
                if (isDebugMode) logDebug("Screenshot container located. Starting render...");
                setRecordingProgress(30); // Pre-canvas
                try {
                    const isIOS = (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') ||
                        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                        (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /Macintosh/.test(navigator.userAgent));
                    if (isIOS) {
                        throw new Error('Skipping html-to-image on iOS/Safari due to SVG foreignObject rendering security and canvas limitations');
                    }
                    if (isDebugMode) logDebug("Attempting html-to-image toPng render...");

                    let data = '';
                    let attempts = 0;
                    while (attempts < 3) {
                        data = await Promise.race([
                            toPng(screenshotRef.current!, {
                                pixelRatio: 2,
                                backgroundColor: 'transparent'
                            }),
                            new Promise((_, reject) => setTimeout(() => reject(new Error('html-to-image timeout')), 15000))
                        ]) as string;

                        if (data && data.length > 100 && data !== 'data:,') break;
                        attempts++;
                        await new Promise(resolve => setTimeout(resolve, 250)); // Wait before retry
                    }

                    if (!data || data.length < 100 || data === 'data:,') {
                        throw new Error('html-to-image returned empty data after 3 attempts');
                    }

                    dataUrl = data;
                    if (isDebugMode) logDebug("toPng render successful.");
                } catch (e) {
                    if (isDebugMode) logDebug(`toPng failed: ${e}. Falling back to html2canvas...`);
                    console.warn("html-to-image failed, falling back to html2canvas", e);
                    setRecordingProgress(50);
                    const canvas = await Promise.race([
                        html2canvas(screenshotRef.current!, {
                            useCORS: true,
                            allowTaint: false,
                            scale: 2,
                            backgroundColor: null
                        }),
                        new Promise((_, reject) => setTimeout(() => reject(new Error('html2canvas timeout')), 20000))
                    ]) as HTMLCanvasElement;

                    const canvasData = canvas.toDataURL('image/png');
                    if (!canvasData || canvasData.length < 100 || canvasData === 'data:,') {
                        throw new Error('html2canvas returned empty data');
                    }
                    dataUrl = canvasData;
                    if (isDebugMode) logDebug("html2canvas render successful.");
                }
                setRecordingProgress(100);
            } else {
                if (isDebugMode) logDebug("ERROR: screenshotRef.current is missing!");
                alert("Debug: screenshotRef is missing!");
            }
        } catch (err: any) {
            if (isDebugMode) logDebug(`ERROR during screenshot render: ${err.message || err}`);
            console.error("Failed to generate screenshot", err);
            alert("Error generating image: " + (err.message || err.toString()));
        }

        setIsGenerating(false);

        // Video Format External Share Flow (Instagram, TikTok, Download, Share)
        if (isVideoFormat && (platform === 'instagram' || platform === 'tiktok' || platform === 'download' || platform === 'other')) {
            if (isDebugMode) logDebug("Routing video post format to recorder...");
            handleVideoShare(platform);
            return;
        }

        setRecordingStatus('idle'); // Hide overlay

        if (!dataUrl) return;

        // Action block
        if (platform === 'download') {
            const ext = dataUrl.startsWith('data:video') ? 'webm' : 'png';

            try { await copyToClipboard(postUrl); } catch (e) { }

            if (Capacitor.isNativePlatform()) {
                try {
                    const base64Data = dataUrl.split(',')[1];
                    const fileName = `goodlyfe_post_${post.id}_${Date.now()}.${ext}`;

                    if (Capacitor.getPlatform() === 'ios') {
                        // On iOS, saving to Documents is hidden from the user. 
                        // We save to Cache and trigger Share so they can 'Save Image' to their gallery.
                        const savedFile = await Filesystem.writeFile({
                            path: fileName,
                            data: base64Data,
                            directory: Directory.Cache
                        });
                        await Share.share({
                            files: [savedFile.uri]
                        });
                    } else {
                        // Android
                        const dir = Directory.ExternalStorage;
                        const savePath = `Download/${fileName}`;

                        await Filesystem.writeFile({
                            path: savePath,
                            data: base64Data,
                            directory: dir
                        });
                        alert(`Saved to Downloads and link copied!`);
                    }
                } catch (e) {
                    console.error("Download fail:", e);
                    alert("Download failed. Missing permissions?");
                }
            } else {
                try {
                    const a = document.createElement('a');
                    a.href = dataUrl;
                    a.download = `goodlyfe_post_${post.id}.${ext}`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    alert("Downloaded and link copied!");
                } catch (e) {
                    console.error("Web download fail:", e);
                    alert("Download failed on web.");
                }
            }
            return;
        }

        if (platform === 'copy') {
            try { await copyToClipboard(postUrl); } catch (e) { }

            const res = await fetch(dataUrl);
            const b = await res.blob();

            if (Capacitor.isNativePlatform()) {
                try {
                    const { Clipboard } = await import('@capacitor/clipboard');
                    await Clipboard.write({
                        url: postUrl,
                        image: dataUrl
                    });
                    alert("Image and link copied!");
                } catch (err) {
                    console.warn(err);
                    alert("Link copied! (Native image copy failed)");
                }
            } else {
                if (b.type.startsWith('image/') && (window as any).ClipboardItem && navigator.clipboard) {
                    try {
                        const item = new ClipboardItem({ [b.type]: b });
                        await navigator.clipboard.write([item]);
                        alert("Image and link copied!");
                    } catch (e) {
                        alert("Link copied! (Browser blocked image copy)");
                    }
                } else {
                    alert("Link copied!");
                }
            }
            return;
        }

        if (Capacitor.isNativePlatform()) {
            if (platform === 'instagram' && type === 'story') {
                if (!(window as any).Instagram) {
                    alert("Debug: window.Instagram is undefined. Plugin not loaded.");
                } else {
                    (window as any).Instagram.isInstalled((err: any, installed: boolean) => {
                        if (installed) {
                            (window as any).Instagram.share(dataUrl!, shareCaption, (shareErr: any) => {
                                if (shareErr) alert("Instagram plugin error: " + (shareErr.message || shareErr.toString()));
                            });
                        } else {
                            alert("Instagram is not installed! (According to plugin)");
                        }
                    });
                }
                return;
            }

            if (platform === 'twitter' || platform === 'whatsapp' || platform === 'tiktok' || platform === 'other') {
                try {
                    const base64Data = dataUrl.split(',')[1];
                    const fileName = `goodlyfe_${isProfileShare ? 'profile' : 'post'}_${post.id}_${Date.now()}.png`;
                    const savedFile = await Filesystem.writeFile({
                        path: fileName, data: base64Data, directory: Directory.Cache
                    });
                    await Share.share({
                        title: isProfileShare ? 'GoodLyfe Profile' : 'GoodLyfe Post',
                        text: shareCaption,
                        url: postUrl,
                        files: [savedFile.uri]
                    });
                } catch (e) { }
                return;
            }
        } else {
            // WEB / PWA Share API
            if ((platform === 'other' || platform === 'instagram' || platform === 'tiktok') && navigator.share) {
                const blob = await fetch(dataUrl).then(r => r.blob());
                const files = [new File([blob], 'share.png', { type: 'image/png' })];
                try {
                    await navigator.share({
                        title: 'GoodLyfe', text: shareCaption, url: postUrl,
                        files: (navigator.canShare && navigator.canShare({ files })) ? files : undefined
                    });
                } catch (e) { }
                return;
            }
        }
    };

    const SocialButton = ({ platform, type, label, color, icon }: { platform: string, type: 'post' | 'story' | 'message' | 'download' | 'copy', label: string, color: string, icon: React.ReactNode }) => (
        <button
            onClick={() => {
                handleSocialShare(platform, type);
                onExternalShare?.();
            }}
            disabled={isGenerating || isRecording}
            className={`flex flex-col items-center gap-1 p-2 transition-transform hover:scale-110 active:scale-95 ${isGenerating || isRecording ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white shadow-lg`} style={{ backgroundColor: color }}>
                {icon}
            </div>
            <span className="text-[10px] text-gray-400 font-medium">{label}</span>
        </button>
    );

    const blurOpacity = usersData[currentUser || '']?.postBlurOpacity ?? 80;

    const filteredCommunities = communities.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c._id.toLowerCase().includes(search.toLowerCase())
    );

    const renderTargetRow = (t: ShareTarget) => {
        const isSelected = selectedTargetIds.includes(t.id);
        return (
            <label key={t.id} className="flex items-center gap-3 py-2 cursor-pointer hover:bg-black/20 rounded-full px-2">
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={e => setSelectedTargetIds(sel => e.target.checked ? [...sel, t.id] : sel.filter(id => id !== t.id))}
                    className="sr-only"
                />

                <div className={`flex-none w-9 h-9 min-w-[36px] min-h-[36px] rounded-full overflow-hidden flex items-center justify-center transition-colors ${isSelected ? 'border-transparent' : 'bg-transparent border border-white/10'}`}>
                    <img
                        src="/assets/checkmark.webp"
                        alt="selected"
                        onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                        style={{
                            transform: isSelected ? 'scale(1)' : 'scale(0.7)',
                            transition: 'transform 450ms cubic-bezier(.2,.9,.2,1), opacity 450ms',
                            opacity: isSelected ? 1 : 0
                        }}
                        className="w-6 h-6"
                    />
                </div>

                {t.avatarUrl ? (
                    <img src={t.avatarUrl} alt={t.name} className="w-9 h-9 rounded-full object-cover" />
                ) : t.type === 'channel' ? (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {t.name[0]?.toUpperCase()}
                    </div>
                ) : t.type === 'yipyap' ? (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        G
                    </div>
                ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                        {t.name[0]?.toUpperCase()}
                    </div>
                )}

                <span className="text-sm font-black italic uppercase tracking-tighter truncate" style={{ ...stickerTextStyle(0.18), color: textColor }}>{t.name}</span>
            </label>
        );
    };

    return isOpen ? (
        <>
            {(post.visibility === 'public' || isProfileShare || currentUser === post.author) && (
                <PostScreenshot
                    ref={screenshotRef}
                    post={post}
                    authorData={usersData[post.author]}
                    postBlurOpacity={blurOpacity}
                    mediaIndex={currentSlideIndex}
                    bgOverrideUrl={selectedBgSlideIndex !== null ? (authorSlideshowImages[selectedBgSlideIndex]?.url ?? undefined) : undefined}
                    postsCount={posts.filter(p => p.author === post.author).length}
                    imageBgMode={(isImagePost || isVideoOrAudioPost) ? imageBgMode : undefined}
                    communities={communities}
                />
            )}
            <div
                className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4"
                onClick={onClose}
            >
                <div
                    className="rounded-[2.5rem] shadow-2xl max-w-xl w-full mx-4 max-h-[90vh] flex flex-col border border-white/10 overflow-hidden"
                    style={{ backgroundColor: modalBg }}
                    onClick={e => e.stopPropagation()}
                >
                    <div className="p-8 pb-4 flex items-center justify-between border-b border-white/5">
                        <h3 className="text-2xl font-black italic uppercase tracking-tighter" style={{ ...stickerTextStyle(0.18), color: textColor }}>Spread the word</h3>
                        <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-xl transition-colors" style={{ color: textColor }}>
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto no-scrollbar p-8 pt-4 allow-scroll space-y-4">
                        <input
                            ref={searchRef}
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search users..."
                            className="w-full p-3 rounded-2xl bg-black/20 focus:outline-none focus:ring-2 focus:ring-blue-500/50 border border-white/5"
                            style={{ ...stickerTextStyle(0.18), color: textColor }}
                        />
                        <div className="max-h-[45vh] overflow-y-auto mb-4 space-y-3 touch-pan-y overscroll-contain pr-1">
                            {filteredShareTargets.length === 0 && <div className="px-1 font-black italic uppercase tracking-tighter" style={{ ...stickerTextStyle(0.18), color: textColor, opacity: 0.5 }}>No recipients found.</div>}
                            
                            {/* Direct Messages Section */}
                            {groupedShareTargets.dms.length > 0 && (
                                <div className="space-y-1">
                                    <button 
                                        onClick={() => setDmsExpanded(!dmsExpanded)} 
                                        className="w-full flex items-center justify-between py-1.5 px-2 hover:bg-white/5 rounded-xl transition-colors select-none text-left"
                                        style={{ color: textColor }}
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60" style={{ ...stickerTextStyle(0.18) }}>Direct Messages ({groupedShareTargets.dms.length})</span>
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-200 transform ${dmsExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    {dmsExpanded && (
                                        <div className="space-y-1 pl-1">
                                            {groupedShareTargets.dms.map(t => renderTargetRow(t))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* YipYap Groups Section */}
                            {groupedShareTargets.yipyaps.length > 0 && (
                                <div className="space-y-1">
                                    <button 
                                        onClick={() => setYipyapsExpanded(!yipyapsExpanded)} 
                                        className="w-full flex items-center justify-between py-1.5 px-2 hover:bg-white/5 rounded-xl transition-colors select-none text-left"
                                        style={{ color: textColor }}
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60" style={{ ...stickerTextStyle(0.18) }}>YipYap Conversations ({groupedShareTargets.yipyaps.length})</span>
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-200 transform ${yipyapsExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    {yipyapsExpanded && (
                                        <div className="space-y-1 pl-1">
                                            {groupedShareTargets.yipyaps.map(t => renderTargetRow(t))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Communities Section */}
                            {groupedShareTargets.channels.length > 0 && (
                                <div className="space-y-1">
                                    <button 
                                        onClick={() => setCommunitiesExpanded(!communitiesExpanded)} 
                                        className="w-full flex items-center justify-between py-1.5 px-2 hover:bg-white/5 rounded-xl transition-colors select-none text-left"
                                        style={{ color: textColor }}
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60" style={{ ...stickerTextStyle(0.18) }}>Community Channels ({groupedShareTargets.channels.length})</span>
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-200 transform ${communitiesExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    {communitiesExpanded && (
                                        <div className="space-y-1 pl-1">
                                            {groupedShareTargets.channels.map(t => renderTargetRow(t))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Duration selector moved to Preview Overlay - but we need a trigger button */}
                        {isVideoShareMode && (
                            <div className="border-t border-white/10 pt-4 mb-4 flex justify-between items-center px-1">
                                <div className="flex flex-col">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50" style={{ ...stickerTextStyle(0.18), color: textColor }}>Clip Selection</p>
                                    <p className="text-[10px] text-blue-400 font-black italic tracking-tighter" style={{ ...stickerTextStyle(0.18), color: textColor }}>Select Start/Stop in Preview</p>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        // Trigger AudioContext resume on user gesture
                                        try {
                                            const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
                                            const ctx = new AudioContextClass();
                                            if (ctx.state === 'suspended') ctx.resume();
                                        } catch (e) { }
                                        setIsPreviewing(!isPreviewing);
                                    }}
                                    className={`text-[10px] px-4 py-1.5 rounded-full font-bold uppercase tracking-wider transition-all border ${isPreviewing ? 'bg-red-500 text-white border-red-500 animate-pulse' : 'bg-blue-500/10 text-blue-400 border-blue-500/30 hover:bg-blue-500 hover:text-white'}`}
                                >
                                    {isPreviewing ? 'Close Preview' : 'Preview & Clip'}
                                </button>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 mb-3 mt-4">
                            <button onClick={onClose} className="hover:bg-white/10 font-black italic uppercase tracking-tighter py-3 px-6 rounded-xl transition-colors" style={{ ...stickerTextStyle(0.18), color: textColor }}>Cancel</button>
                            <button
                                onClick={() => {
                                    if (selectedTargetIds.length > 0) {
                                        const bgUrl = selectedBgSlideIndex !== null ? (authorSlideshowImages[selectedBgSlideIndex]?.url ?? undefined) : undefined;
                                        const targets = selectedTargetIds.map(id => filteredShareTargets.find(t => t.id === id)).filter(Boolean) as ShareTarget[];
                                        onShare(targets, bgUrl);
                                    }
                                }}
                                disabled={selectedTargetIds.length === 0}
                                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-500 disabled:opacity-50 text-white font-black italic uppercase tracking-tighter py-3 px-6 rounded-xl transition-colors"
                                style={stickerTextStyle(0.18)}
                            >Share</button>
                        </div>

                        {/* Background image picker & Layout Selector for text posts, profile shares, and media posts */}
                        {(isImagePost || isVideoOrAudioPost || isTextPostOnly || isProfileShare) && (
                            <div className="border-t border-white/10 pt-4 mb-2">
                                <div className="flex items-center justify-between mb-3 px-1">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50" style={{ ...stickerTextStyle(0.18), color: textColor }}>
                                        {isImagePost ? 'Featured Image' : isVideoOrAudioPost ? (post.type === 'voice' || post.type === 'audio' ? 'Audio Layout' : 'Video Layout') : 'Background Image'}
                                    </p>
                                    {(isImagePost || isVideoOrAudioPost) && (
                                        <div className="flex items-center gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => setImageBgMode('border')}
                                                className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg transition-all ${imageBgMode === 'border'
                                                        ? 'bg-white/20 text-white'
                                                        : 'text-white/30 hover:text-white/60'
                                                    }`}
                                            >Border</button>
                                            <div
                                                onClick={() => setImageBgMode(m => m === 'borderless' ? 'border' : 'borderless')}
                                                className="relative w-10 h-5 rounded-full cursor-pointer transition-colors flex-shrink-0"
                                                style={{ backgroundColor: imageBgMode === 'borderless' ? themeColor : 'rgba(255,255,255,0.15)' }}
                                            >
                                                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${imageBgMode === 'borderless' ? 'left-5' : 'left-0.5'
                                                    }`} />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setImageBgMode('borderless')}
                                                className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-lg transition-all ${imageBgMode === 'borderless'
                                                        ? 'bg-white/20 text-white'
                                                        : 'text-white/30 hover:text-white/60'
                                                    }`}
                                            >Borderless</button>
                                        </div>
                                    )}
                                </div>

                                {authorSlideshowImages.length > 0 && (
                                    <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                                        {authorSlideshowImages.map((img, idx) => (
                                            <button
                                                key={img.id || idx}
                                                type="button"
                                                onClick={() => setSelectedBgSlideIndex(idx)}
                                                className={`flex-none w-16 h-16 rounded-2xl border-2 overflow-hidden transition-all ${selectedBgSlideIndex === idx
                                                        ? 'border-white/80 scale-105 shadow-lg'
                                                        : 'border-white/20 opacity-60 hover:opacity-90'
                                                    }`}
                                                title={img.name || `Slide ${idx + 1}`}
                                            >
                                                <img
                                                    src={img.url}
                                                    alt={img.name || `Slide ${idx + 1}`}
                                                    className="w-full h-full object-cover"
                                                    onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                                                />
                                            </button>
                                        ))}
                                        {/* None / default option */}
                                        <button
                                            type="button"
                                            onClick={() => setSelectedBgSlideIndex(null)}
                                            className={`flex-none w-16 h-16 rounded-2xl border-2 transition-all flex items-center justify-center text-xs font-black uppercase ${selectedBgSlideIndex === null
                                                    ? 'border-white/80 scale-105 shadow-lg'
                                                    : 'border-white/20 opacity-60 hover:opacity-90'
                                                }`}
                                            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
                                            title="Original Background"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <circle cx="12" cy="12" r="10" strokeWidth={1.5} />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-12.728 12.728" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {!restrictSharing && (
                            <div className="border-t border-white/5 pt-6 pb-2">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-50 mb-4 px-1 text-center" style={{ ...stickerTextStyle(0.18), color: textColor }}>Share Options</p>
                                <div className="flex overflow-x-auto gap-4 pb-2 no-scrollbar justify-center">
                                    <SocialButton
                                        platform="copyurl" type="copy" label="Copy URL" color="#10B981"
                                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>}
                                    />
                                    <SocialButton
                                        platform="download" type="download" label="Download" color="#3B82F6"
                                        icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>}
                                    />
                                    <SocialButton
                                        platform="other" type="post" label="Share" color="#6B7280"
                                        icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>}
                                    />
                                </div>
                                {isAdmin && (
                                    <button
                                        onClick={async () => {
                                            setIsDebugMode(true);
                                            setDebugLogs([]);
                                            logDebug("Starting Admin Debug Share Process...");
                                            logDebug(`Post ID: ${post.id}`);
                                            logDebug(`Post Author: ${post.author}`);
                                            logDebug(`Post type: ${post.type}`);
                                            logDebug(`Is video format: ${isVideoShareMode}`);
                                            handleSocialShare('download', 'download');
                                        }}
                                        className="mt-4 w-full bg-red-600/20 hover:bg-red-600/30 text-red-400 font-mono text-xs py-2.5 px-4 rounded-xl border border-red-500/20 transition-all flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                        </svg>
                                        Admin Debug Gen (No Share Count)
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {(isRecording || isPreviewing) && (
                <>
                    {isPreviewing && (
                        <div
                            className="fixed inset-0 bg-black/80 z-[250] animate-fade-in flex flex-col items-center justify-center pointer-events-none"
                            onClick={() => setIsPreviewing(false)}
                        >
                            <div className="text-white font-bold mb-[600px] text-lg pointer-events-auto bg-black/40 px-6 py-3 rounded-full border border-white/10 backdrop-blur-md cursor-pointer hover:bg-black/60 transition-all flex items-center gap-2" onClick={() => setIsPreviewing(false)}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Close Preview
                            </div>
                        </div>
                    )}
                    <PostVideoSharePreview
                        post={post}
                        authorData={usersData[post.author]}
                        postBlurOpacity={blurOpacity}
                        canvasRef={canvasRef}
                        startTime={startTime}
                        endTime={endTime}
                        mediaDuration={mediaDuration}
                        onAudioStreamReady={setAudioStream}
                        mediaIndex={currentSlideIndex}
                        isPreviewing={isPreviewing}
                        setStartTime={setStartTime}
                        setEndTime={setEndTime}
                    />
                </>
            )}

            {recordingStatus !== 'idle' && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-xl animate-fade-in text-center p-6 animate-in fade-in duration-200">
                    {isDebugMode ? (
                        <div className="bg-black/95 rounded-3xl p-6 border border-red-500/30 shadow-2xl max-w-lg w-full flex flex-col gap-4 text-left font-mono">
                            <div className="flex justify-between items-center border-b border-white/10 pb-3">
                                <span className="text-red-400 font-bold text-sm">ADMIN SHARE DEBUG LOGS</span>
                                <button
                                    onClick={() => {
                                        handleCancelGeneration();
                                        setIsDebugMode(false);
                                    }}
                                    className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors text-xs uppercase"
                                >
                                    Cancel
                                </button>
                            </div>
                            <div
                                className="flex-1 max-h-[350px] min-h-[250px] overflow-y-auto space-y-1.5 p-3 rounded-xl bg-black/50 border border-white/5 text-[11px] text-green-400 scroll-smooth pointer-events-auto select-text allow-scroll"
                                onPointerDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                onTouchMove={(e) => e.stopPropagation()}
                                onTouchEnd={(e) => e.stopPropagation()}
                            >
                                {debugLogs.map((log, idx) => (
                                    <div key={idx} className="whitespace-pre-wrap leading-relaxed border-b border-white/5 pb-1">{log}</div>
                                ))}
                                {recordingStatus === 'recording' && (
                                    <div className="text-blue-400 animate-pulse mt-2 font-bold">
                                        Recording Progress: {Math.round(recordingProgress)}%
                                    </div>
                                )}
                            </div>
                            <div className="text-[10px] text-gray-500 text-center">
                                Downloading generated post share formatted as External. No share count is added.
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white/10 rounded-3xl p-8 border border-white/20 shadow-2xl max-w-sm w-full flex flex-col items-center gap-6">
                            {recordingStatus === 'recording' ? (
                                <>
                                    <div className="w-full flex justify-end -mt-4 -mr-4 mb-2">
                                        <button
                                            onClick={handleCancelGeneration}
                                            className="p-2 hover:bg-white/10 rounded-full text-gray-400 hover:text-white transition-colors"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="relative">
                                        <div className="w-16 h-16 border-4 border-blue-500/20 border-t-white rounded-full animate-spin shadow-2xl" />
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse shadow-[0_0_15px_rgba(96,165,250,0.8)]" />
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <p className="text-white text-2xl font-bold text-raised drop-shadow-lg tracking-wide">
                                            Generating goodness...
                                        </p>
                                        <p className="text-blue-400 font-bold text-lg animate-pulse">
                                            {Math.round(recordingProgress)}%
                                        </p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(59,130,246,0.5)]">
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <h3 className="text-white text-2xl font-bold">Ready!</h3>
                                        <p className="text-blue-300 font-medium">Link copied to clipboard</p>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2.5 w-full">
                                        <button
                                            onClick={() => {
                                                if (!generatedBlob) return;
                                                const isImage = generatedBlob.type.startsWith('image/');
                                                const extension = isImage ? 'png' : 'webm';
                                                const url = URL.createObjectURL(generatedBlob);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = `goodlyfe_post_${post.id}.${extension}`;
                                                document.body.appendChild(a);
                                                a.click();
                                                document.body.removeChild(a);
                                                window.URL.revokeObjectURL(url);
                                            }}
                                            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3.5 px-6 rounded-2xl transition-all shadow-lg active:scale-95 text-sm"
                                        >
                                            Download Post
                                        </button>
                                        <button
                                            onClick={async () => {
                                                if (!generatedBlob) return;
                                                const postUrl = isProfileShare
                                                    ? `https://goodlyfe.net/profile/${encodeURIComponent(post.author)}`
                                                    : `https://goodlyfe.net/post/${post.id}`;

                                                // Copy Link Automatically
                                                if (navigator.clipboard) {
                                                    try { await navigator.clipboard.writeText(postUrl); } catch (e) { }
                                                }

                                                const isImage = generatedBlob.type.startsWith('image/');

                                                if (Capacitor.isNativePlatform()) {
                                                    const reader = new FileReader();
                                                    reader.readAsDataURL(generatedBlob);
                                                    reader.onloadend = async () => {
                                                        const base64 = (reader.result as string).split(',')[1];
                                                        const fileName = `goodlyfe_share_${Date.now()}.${isImage ? 'png' : 'webm'}`;
                                                        const savedFile = await Filesystem.writeFile({
                                                            path: fileName,
                                                            data: base64,
                                                            directory: Directory.Cache
                                                        });
                                                        await Share.share({
                                                            title: 'GoodLyfe Post',
                                                            text: `Check out this post on GoodLyfe!\n\n${postUrl}`,
                                                            files: [savedFile.uri]
                                                        });
                                                    };
                                                } else {
                                                    // WEB/PWA: Copy media to clipboard if it's an image
                                                    if (isImage && (window as any).ClipboardItem && navigator.clipboard) {
                                                        try {
                                                            const item = new ClipboardItem({ [generatedBlob.type]: generatedBlob });
                                                            await navigator.clipboard.write([item]);
                                                        } catch (e) {
                                                            console.warn("Failed to copy image to clipboard", e);
                                                        }
                                                    }

                                                    // Trigger Web Share API if available
                                                    if (navigator.share) {
                                                        const isImage = generatedBlob.type.startsWith('image/');
                                                        const file = new File([generatedBlob], `post.${isImage ? 'png' : 'webm'}`, { type: generatedBlob.type });
                                                        try {
                                                            // Try sharing with file FIRST
                                                            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                                                                await navigator.share({
                                                                    title: 'GoodLyfe Post',
                                                                    text: `Check out this post on GoodLyfe!`,
                                                                    url: postUrl,
                                                                    files: [file]
                                                                });
                                                            } else {
                                                                // Fallback to text/url only share
                                                                await navigator.share({
                                                                    title: 'GoodLyfe Post',
                                                                    url: postUrl
                                                                });
                                                            }
                                                        } catch (e) {
                                                            // Last resort fallback
                                                            await navigator.share({
                                                                title: 'GoodLyfe Post',
                                                                url: postUrl
                                                            }).catch(() => { });
                                                        }
                                                    } else {
                                                        alert(isImage ? "Image and link copied!" : "Link copied! Media ready for download below.");
                                                    }
                                                }
                                            }}
                                            className="w-full bg-white/10 hover:bg-white/15 text-white font-bold py-3.5 px-6 rounded-2xl transition-all border border-white/20 text-sm"
                                        >
                                            Copy Post
                                        </button>
                                        <button
                                            onClick={async () => {
                                                const postUrl = isProfileShare
                                                    ? `https://goodlyfe.net/profile/${encodeURIComponent(post.author)}`
                                                    : `https://goodlyfe.net/post/${post.id}`;

                                                if (Capacitor.isNativePlatform()) {
                                                    await Share.share({
                                                        title: isProfileShare ? 'GoodLyfe Profile' : 'GoodLyfe Post',
                                                        url: postUrl,
                                                        dialogTitle: 'Share Post URL'
                                                    });
                                                } else if ((navigator as any).share) {
                                                    try {
                                                        await (navigator as any).share({
                                                            title: isProfileShare ? 'GoodLyfe Profile' : 'GoodLyfe Post',
                                                            url: postUrl
                                                        });
                                                    } catch (e) { }
                                                } else {
                                                    if (navigator.clipboard) {
                                                        await navigator.clipboard.writeText(postUrl);
                                                        alert("URL copied to clipboard!");
                                                    } else {
                                                        // Fallback for non-secure contexts
                                                        const textArea = document.createElement("textarea");
                                                        textArea.value = postUrl;
                                                        document.body.appendChild(textArea);
                                                        textArea.select();
                                                        try {
                                                            document.execCommand('copy');
                                                            alert("URL copied to clipboard!");
                                                        } catch (err) {
                                                            alert("Share URL: " + postUrl);
                                                        }
                                                        document.body.removeChild(textArea);
                                                    }
                                                }
                                            }}
                                            className="w-full bg-white/5 hover:bg-white/10 text-white font-semibold py-3 px-6 rounded-2xl transition-all border border-white/10 text-sm"
                                        >
                                            Share Post URL
                                        </button>
                                        <button
                                            onClick={() => {
                                                setRecordingStatus('idle');
                                                setGeneratedBlob(null);
                                            }}
                                            className="w-full bg-white/5 hover:bg-white/10 text-white font-semibold py-3 px-6 rounded-2xl transition-all border border-white/10 text-sm"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    ) : null;
};
